import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalSubTitle } from '../comps';
import { FormikProvider, useFormik } from 'formik';
import { Poppins, Spacer } from 'src/common';
import { Guard } from '../Guard';
import { ActionType, FormValues, getInitialValuesForAction, getValidationSchemaForAction } from './types';
import { AddComment } from './forms';
import { EventKind, ObjectKind, useUpsertEventMutation } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';

const StyledModal = styled(Modal)`
  width: 950px;
  min-height: 200px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }

  .radio-row {
    display: flex;
    align-items: center;
    grid-gap: 11px;
    margin-bottom: 12px;
    cursor: pointer;
  }
`;

const ErrorLabel = styled.div`
  margin-top: 15px;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.error};
  font-weight: 400;
`;

export type CommentsModalData = { objectType: ObjectKind; objectId: string };

interface CommentsModalProps {
  onClose: () => void;
  subTitle?: { title: string; value: string };
  initialValues?: Partial<FormValues>;
  defaultAction?: ActionType;
  eventData: CommentsModalData;
  refetch?: () => void;
}

export const CommentsModal: React.FC<CommentsModalProps> = ({
  onClose,
  subTitle,
  initialValues,
  defaultAction = 'add-comment',
  eventData,
  refetch,
}) => {
  const { workspaceId } = useAuth();
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [currentAction, setCurrentAction] = useState<ActionType>(initialValues?.action || defaultAction);

  const onCompleted = async () => {
    refetch?.();
    onClose();
  };

  const [upsertEvent, { error }] = useUpsertEventMutation({ onCompleted });

  const formik = useFormik<FormValues>({
    initialValues: getInitialValuesForAction(currentAction, initialValues),
    validationSchema: getValidationSchemaForAction(currentAction),
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (values.action === 'add-comment') {
        return upsertEvent({
          variables: {
            workspaceId,
            eventData: [
              {
                eventType: EventKind.Comment,
                notes: values.addCommentData.comment,
                ...eventData,
              },
            ],
          },
        });
      }

      return;
    },
  });

  const handleActionChange = (newAction: ActionType) => {
    setCurrentAction(newAction);
    const newInitialValues = getInitialValuesForAction(newAction);

    formik.resetForm({
      values: newInitialValues,
      errors: {},
      touched: {},
    });
  };

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={formik.dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen center={true}>
          <Spacer $px={40} />
          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              Add comments
            </Poppins>
            {subTitle && <ModalSubTitle title={subTitle.title} value={subTitle.value} />}
            {/* <Spacer $px={24} /> */}
            <FormikProvider value={formik}>
              {/* <Poppins px={14} weight={400} color="cflowerBlue">
                What would you like to do?
              </Poppins> */}
              {/* <Spacer $px={16} />

              {options.map((el) => (
                <div className="radio-row" key={el.value} onClick={() => handleActionChange(el.value)}>
                  <Radio
                    medium
                    isChecked={formik.values.action !== el.value}
                    onClick={() => handleActionChange(el.value)}
                    dataCy={`radio-${el.value}`}
                  />
                  <Poppins px={14} weight={500}>
                    {el.label}
                  </Poppins>
                </div>
              ))} */}
              <Spacer $px={32} />
              {formik.values.action === 'add-comment' && <AddComment />}
            </FormikProvider>
          </div>
          {error && <ErrorLabel>{error.message || 'Something went wrong'}</ErrorLabel>}
          <Spacer $px={40} />
        </StyledModal>
      }
    />
  );
};
